<template>
    <ModalComponent :is-open="isModalOpen" :show-footer-buttons="true" :title="modalTitle" size="lg" position="center" @update:isOpen="handleClose">
        <template #header></template>
        <div class="p-4 space-y-4">
            <!-- Tag Name Field -->
            <div>
                <Label for="tag-name">
                    Tag Name <span v-if="!unchangedTag" class="text-destructive">*</span>
                </Label>
                <Input
                    id="tag-name"
                    v-model="currentTag.name"
                    placeholder="Enter tag name"
                    class="mt-2"
                />
            </div>
            <!-- Description Field -->
            <div>
                <Label for="tag-description">Description</Label>
                <Textarea
                    id="tag-description"
                    v-model="currentTag.description"
                    rows="4"
                    placeholder="Enter tag description (optional)"
                    class="mt-2"
                />
            </div>
            <!-- Tag Types Multiselect -->
            <div>
                <Label for="tag-types">
                    Tag Types <span v-if="!unchangedTag" class="text-destructive">*</span>
                </Label>
                <MultiSelect
                    id="tag-types"
                    v-model="selectedTypeValues"
                    :options="TagTypeCreateOptions"
                    placeholder="Select tag types"
                    :option-label="'name'"
                    :track-by="'value'"
                    :show-tags="false"
                    class="mt-2"
                />
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end space-x-2">
                <Button @click="handleCancel" variant="secondary">
                    Cancel
                </Button>
                <LoadingButton :loading="saveIsLoading" @click="handleSave" :disabled="!enableSave">Save</LoadingButton>
            </div>
        </template>
    </ModalComponent>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import ModalComponent from '@/components/global/modals/Modal.vue';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LoadingButton } from '@/components/ui/loading-button';
import { TagTypeCreateOptions, TagTypesInfo } from '@/utils/constants';
import _ from 'lodash';
import { useLoaderStore } from '@/stores/loader.store';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    tag: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update:isOpen', 'save']);

const isModalOpen = ref(false);
const unchangedTag = ref(null);
const loaderStore = useLoaderStore();

const saveIsLoading = computed(() => loaderStore.isLoading(`tags.update`) || loaderStore.isLoading('tags.create'));

const selectedTypeValues = computed({
    get() {
        return currentTag.value.type.map(type => type.value);
    },
    set(values) {
        currentTag.value.type = values.map(value =>
            TagTypeCreateOptions.find(option => option.value === value)
        ).filter(Boolean);
    }
});

watch(
    () => props.isOpen,
    (newVal) => {
        isModalOpen.value = newVal;
    }
);

const modalTitle = computed(() => (props.tag ? 'Edit Tag' : 'Add Tag'));

const currentTag = ref({
    id: null,
    name: '',
    description: '',
    type: [],
    system: false
});

onMounted(() => {
    isModalOpen.value = props.isOpen;

    if (props.tag) {
        unchangedTag.value = _.cloneDeep(props.tag || null);

        currentTag.value = {
            ...props.tag,
            type: TagTypesInfo.filter((item) => props.tag.type.includes(item.value))
        };
    }
});

const enableSave = computed(() => {
    const payload = {
        ...currentTag.value,
        type: currentTag.value.type.map((item) => item.value)
    };

    return !!payload.name.trim() && payload.type.length > 0 && (!unchangedTag.value || !_.isEqual(payload, unchangedTag.value));
});

const handleClose = (value) => {
    isModalOpen.value = value;

    if (!value) {
        emit('update:isOpen', false);
    }
};

const handleCancel = () => {
    emit('update:isOpen', false);
};

const handleSave = () => {
    if (!currentTag.value.name.trim() || currentTag.value.type.length === 0) {
        alert('Please provide a tag name and select at least one tag type.');
        return;
    }

    let tagPayload = {
        ...currentTag.value,
        type: currentTag.value.type.map((item) => item.value)
    };

    if (unchangedTag.value && unchangedTag.value.id) {
        // send only the updates/differences from the unchanged one
        tagPayload = {
            id: unchangedTag.value.id,
            name: tagPayload.name !== unchangedTag.value.name ? tagPayload.name : null,
            description: tagPayload.description !== unchangedTag.value.description ? tagPayload.description : unchangedTag.value.description,
            type: _.isEqual(tagPayload.type, unchangedTag.value.type) ? null : tagPayload.type
        };

        delete tagPayload.system;

        tagPayload = _.omitBy(tagPayload, _.isNil);
    } else {
        delete tagPayload.id;
        delete tagPayload.system;
    }

    emit('save', tagPayload);
};
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
